/* Servicio del módulo de Agendamientos (TD-11766).
 * Centraliza las llamadas al backend (prefijo /agenda/...).
 * El token de autenticación lo agrega el interceptor de ./api.
 */
import api from "./api";

export type TipoAgenda = "INDIVIDUAL" | "GRUPAL";
export type ModalidadAgenda = "PRESENCIAL" | "VIRTUAL" | "HIBRIDA";
export type TipoPublico = "GLOBAL" | "ADMINISTRATIVOS" | "OPERARIOS" | "LISTADO";
export type EstadoTema = "BORRADOR" | "PUBLICADO" | "PAUSADO" | "FINALIZADO";
export type EstadoReserva = "PENDIENTE" | "CONFIRMADA" | "RECHAZADA" | "CANCELADA" | "ESPERA";
export type Asistencia = "PRESENTE" | "AUSENTE" | null;
export type Categoria = "SALUD" | "BIENESTAR" | "RRHH" | "CAPACITACION" | "BENEFICIOS" | "OTRO";

// ---------------------------------------------------------------- Gestión

export interface ResponsableForm {
  id?: number | null;
  /** Clave local para React y para que las franjas apunten al responsable. */
  _k: number;
  tipo: "INTERNO" | "EXTERNO";
  cedula?: string | null;
  nombre: string;
  cargo?: string | null;
  empresa?: string | null;
  telefono?: string | null;
  correo?: string | null;
}

export interface FranjaForm {
  _k: number;
  dia_semana: number;
  hora_desde: string;
  hora_hasta: string;
  /** _k del responsable (null = sin responsable). */
  responsable_k: number | null;
}

export interface ExcepcionForm {
  _k: number;
  fecha: string;
  tipo: "BLOQUEO" | "EXTRA";
  hora_desde: string;
  hora_hasta: string;
  responsable_k: number | null;
  motivo: string;
}

export interface PreguntaAgendaForm {
  id?: number | null;
  _k: number;
  texto: string;
  tipo: "TEXTO" | "OPCION";
  opciones: string[];
  obligatoria: boolean;
}

export interface PersonaRef {
  cedula: string;
  nombre: string | null;
}

/** Payload que espera el backend (las franjas apuntan al responsable por índice). */
export interface TemaPayload {
  titulo: string;
  descripcion: string;
  categoria: Categoria;
  tipo_agenda: TipoAgenda;
  modalidad: ModalidadAgenda;
  lugar: string;
  link_virtual: string;
  link_minutos_antes: number | null;
  vigencia_desde: string | null;
  vigencia_hasta: string | null;
  duracion_minutos: number | null;
  pausa_minutos: number | null;
  cupo_por_turno: number | null;
  apertura_inscripcion: string | null;
  anticipacion_min_horas: number | null;
  max_reservas_activas: number | null;
  max_reservas_total: number | null;
  cancelacion_min_horas: number | null;
  requiere_aprobacion: boolean;
  permite_lista_espera: boolean;
  penalizacion_dias: number | null;
  publico_tipo: TipoPublico;
  notificar_publicacion: boolean;
  recordatorio_1_horas: number | null;
  recordatorio_2_horas: number | null;
  notificar_responsable: boolean;
  notificar_correo: boolean;
  encuesta_habilitada: boolean;
  confidencial: boolean;
  responsables: Array<Omit<ResponsableForm, "_k">>;
  franjas: Array<{ dia_semana: number; hora_desde: string; hora_hasta: string; responsable_idx: number | null }>;
  excepciones: Array<{
    fecha: string;
    tipo: "BLOQUEO" | "EXTRA";
    hora_desde: string | null;
    hora_hasta: string | null;
    responsable_idx: number | null;
    motivo: string;
  }>;
  preguntas: Array<Omit<PreguntaAgendaForm, "_k">>;
  publico: {
    cedulas: string[];
    excluidas: string[];
    cargos: string[];
    departamentos: string[];
    unidades: string[];
  };
  gestores: string[];
  publicar?: boolean;
}

export interface TemaResumenGestion {
  id: number;
  titulo: string;
  categoria: Categoria;
  tipo_agenda: TipoAgenda;
  modalidad: ModalidadAgenda;
  estado: EstadoTema;
  vigencia_desde: string | null;
  vigencia_hasta: string | null;
  requiere_aprobacion: boolean;
  proximas: number;
  pendientes: number;
  espera: number;
}

export interface RolTema {
  es_admin: boolean;
  es_gestor: boolean;
  puede_gestionar: boolean;
  responsable_ids: number[];
  puede_ver: boolean;
}

export interface EstadisticasTema {
  total: number;
  proximas: number | null;
  pendientes: number | null;
  espera: number | null;
  canceladas: number | null;
  presentes: number | null;
  ausentes: number | null;
  encuesta_promedio: number | null;
  encuestas: number;
}

export interface TemaDetalle {
  id: number;
  titulo: string;
  descripcion: string | null;
  categoria: Categoria;
  tipo_agenda: TipoAgenda;
  modalidad: ModalidadAgenda;
  lugar: string | null;
  link_virtual: string | null;
  link_minutos_antes: number;
  vigencia_desde: string | null;
  vigencia_hasta: string | null;
  duracion_minutos: number;
  pausa_minutos: number;
  cupo_por_turno: number;
  apertura_inscripcion: string | null;
  anticipacion_min_horas: number;
  max_reservas_activas: number;
  max_reservas_total: number | null;
  cancelacion_min_horas: number;
  requiere_aprobacion: boolean;
  permite_lista_espera: boolean;
  penalizacion_dias: number | null;
  publico_tipo: TipoPublico;
  notificar_publicacion: boolean;
  notificado_publicacion: boolean;
  recordatorio_1_horas: number | null;
  recordatorio_2_horas: number | null;
  notificar_responsable: boolean;
  notificar_correo: boolean;
  encuesta_habilitada: boolean;
  confidencial: boolean;
  estado: EstadoTema;
  creado_por: string;
  responsables: Array<ResponsableForm & { id: number }>;
  franjas: Array<{ id: number; responsable_id: number | null; dia_semana: number; hora_desde: string; hora_hasta: string }>;
  excepciones: Array<{
    id: number;
    responsable_id: number | null;
    fecha: string;
    tipo: "BLOQUEO" | "EXTRA";
    hora_desde: string | null;
    hora_hasta: string | null;
    motivo: string | null;
  }>;
  preguntas: Array<{ id: number; texto: string; tipo: "TEXTO" | "OPCION"; opciones: string[]; obligatoria: boolean }>;
  gestores: PersonaRef[];
  publico: {
    cedulas: PersonaRef[];
    excluidas: PersonaRef[];
    cargos: string[];
    departamentos: string[];
    unidades: string[];
  };
  estadisticas: EstadisticasTema;
  rol: RolTema;
}

export interface ReservaGestion {
  id: number;
  responsable_id: number | null;
  responsable_nombre: string | null;
  fecha_inicio: string;
  fecha_fin: string;
  cedula: string;
  nombre: string | null;
  cargo: string | null;
  departamento: string | null;
  estado: EstadoReserva;
  fecha_creacion: string;
  /** Motivo del rechazo o de la cancelación (según el estado). */
  motivo: string | null;
  fecha_cancelacion: string | null;
  asistencia: Asistencia;
  encuesta_puntaje: number | null;
  encuesta_comentario: string | null;
  /** null = confidencial y no sos el responsable de la cita. */
  respuestas: Array<{ pregunta: string; valor: string }> | null;
  puede_atender: boolean;
  empezo: boolean;
}

export interface Turno {
  inicio: string;
  fin: string;
  responsable_id: number | null;
  responsable_nombre: string | null;
  cupo: number;
  ocupados: number;
  disponibles: number;
  en_espera: number | null;
  mi_estado?: EstadoReserva | null;
}

export interface Funcionario {
  cedula: string;
  legajo: number | null;
  nombre: string;
  cargo: string;
  departamento: string;
}

export interface Catalogos {
  cargos: string[];
  departamentos: string[];
  unidades: string[];
}

// ---------------------------------------------------------------- Usuario

export interface TemaDisponible {
  id: number;
  titulo: string;
  descripcion: string | null;
  categoria: Categoria;
  tipo_agenda: TipoAgenda;
  modalidad: ModalidadAgenda;
  lugar: string | null;
  duracion_minutos: number;
  requiere_aprobacion: boolean;
  responsables: Array<{ nombre: string; detalle: string | null; tipo: "INTERNO" | "EXTERNO" }>;
  mis_activas: number;
  max_reservas_activas: number;
  apertura_inscripcion: string | null;
  inscripcion_abierta: boolean;
}

export interface TemaPublico extends Omit<TemaDisponible, "responsables"> {
  pausa_minutos: number;
  cupo_por_turno: number;
  anticipacion_min_horas: number;
  cancelacion_min_horas: number;
  max_reservas_total: number | null;
  permite_lista_espera: boolean;
  penalizacion_dias: number | null;
  confidencial: boolean;
  vigencia_hasta: string | null;
  responsables: Array<{ id: number; nombre: string; tipo: "INTERNO" | "EXTERNO"; detalle: string | null }>;
  preguntas: Array<{ id: number; texto: string; tipo: "TEXTO" | "OPCION"; opciones: string[]; obligatoria: boolean }>;
  penalizado_hasta: string | null;
}

export interface MiReserva {
  id: number;
  tema_id: number;
  responsable_id: number | null;
  responsable_nombre: string | null;
  fecha_inicio: string;
  fecha_fin: string;
  estado: EstadoReserva;
  /** Motivo del rechazo o de la cancelación (según el estado). */
  motivo: string | null;
  asistencia: Asistencia;
  encuesta_puntaje: number | null;
  titulo: string;
  categoria: Categoria;
  modalidad: ModalidadAgenda;
  lugar: string | null;
  /** Solo viene dentro de la ventana de acceso (N minutos antes hasta el fin). */
  link_virtual: string | null;
  link_desde?: string;
  tema_estado: EstadoTema;
  requiere_aprobacion: boolean;
  puede_cancelar: boolean;
  puede_reprogramar: boolean;
  limite_cancelacion: string | null;
  encuesta_pendiente: boolean;
}

export interface InicioAgenda {
  disponibles: TemaDisponible[];
  proximas: MiReserva[];
  encuestas: Array<{ id: number; fecha_inicio: string; titulo: string }>;
}

export interface ResultadoReserva {
  info: string;
  id: number;
  estado: EstadoReserva;
}

const BASE = "/agenda";
/** Caché por sesión del acceso a la gestión (se borra al cerrar sesión). */
export const AGENDA_ACCESO_KEY = "agenda_acceso_session";

/** Lo emiten las pantallas al reservar/cancelar para que la card del Home se refresque. */
export const AGENDA_CAMBIO = "agenda:cambio";
export const avisarCambioAgenda = () => window.dispatchEvent(new Event(AGENDA_CAMBIO));

const AgendaAPI = {
  /** Cacheado por sesión: el Menu se remonta en cada navegación. */
  async acceso(): Promise<{ es_admin: boolean; gestiona: boolean }> {
    const cache = sessionStorage.getItem(AGENDA_ACCESO_KEY);
    if (cache) {
      try {
        return JSON.parse(cache);
      } catch {
        /* se vuelve a consultar */
      }
    }
    try {
      const { data } = await api.get(`${BASE}/acceso`);
      const acceso = { es_admin: Boolean(data?.es_admin), gestiona: Boolean(data?.gestiona) };
      sessionStorage.setItem(AGENDA_ACCESO_KEY, JSON.stringify(acceso));
      return acceso;
    } catch {
      return { es_admin: false, gestiona: false };
    }
  },

  /** Tras crear un tema el usuario pasa a gestionar: se invalida el caché. */
  olvidarAcceso() {
    sessionStorage.removeItem(AGENDA_ACCESO_KEY);
  },

  async catalogos(): Promise<Catalogos> {
    const { data } = await api.get(`${BASE}/catalogos`);
    return { cargos: data?.cargos || [], departamentos: data?.departamentos || [], unidades: data?.unidades || [] };
  },

  async buscarFuncionarios(q: string): Promise<Funcionario[]> {
    const { data } = await api.get(`${BASE}/funcionarios`, { params: { q } });
    return data?.datos || [];
  },

  async listarGestion(): Promise<{ datos: TemaResumenGestion[]; es_admin: boolean }> {
    const { data } = await api.get(`${BASE}/gestion/temas`);
    return { datos: data?.datos || [], es_admin: Boolean(data?.es_admin) };
  },

  async crear(payload: TemaPayload): Promise<{ id: number; info: string; no_encontrados: string[] }> {
    const { data } = await api.post(`${BASE}/gestion/temas`, payload);
    return data;
  },

  async obtenerGestion(id: number): Promise<TemaDetalle> {
    const { data } = await api.get(`${BASE}/gestion/temas/${id}`);
    return data?.datos;
  },

  async actualizar(id: number, payload: TemaPayload): Promise<{ info: string; no_encontrados: string[] }> {
    const { data } = await api.put(`${BASE}/gestion/temas/${id}`, payload);
    return data;
  },

  async cambiarEstado(id: number, estado: EstadoTema): Promise<{ info: string }> {
    const { data } = await api.post(`${BASE}/gestion/temas/${id}/estado`, { estado });
    return data;
  },

  async turnosGestion(id: number, desde: string, hasta: string): Promise<Turno[]> {
    const { data } = await api.get(`${BASE}/gestion/temas/${id}/turnos`, { params: { desde, hasta } });
    return data?.datos || [];
  },

  async reservasGestion(
    id: number,
    params: { desde?: string; hasta?: string; estado?: string }
  ): Promise<{ datos: ReservaGestion[]; confidencial: boolean }> {
    const { data } = await api.get(`${BASE}/gestion/temas/${id}/reservas`, { params });
    return { datos: data?.datos || [], confidencial: Boolean(data?.confidencial) };
  },

  async responder(reservaId: number, aprobar: boolean, motivo?: string): Promise<{ info: string }> {
    const { data } = await api.post(`${BASE}/gestion/reservas/${reservaId}/responder`, { aprobar, motivo });
    return data;
  },

  async cancelarGestion(reservaId: number, motivo: string): Promise<{ info: string }> {
    const { data } = await api.post(`${BASE}/gestion/reservas/${reservaId}/cancelar`, { motivo });
    return data;
  },

  async asistencia(reservaId: number, asistencia: Asistencia): Promise<{ info: string }> {
    const { data } = await api.post(`${BASE}/gestion/reservas/${reservaId}/asistencia`, { asistencia });
    return data;
  },

  // ---------------- Usuario ----------------

  async inicio(): Promise<InicioAgenda> {
    const { data } = await api.get(`${BASE}/inicio`);
    return { disponibles: data?.disponibles || [], proximas: data?.proximas || [], encuestas: data?.encuestas || [] };
  },

  async misReservas(): Promise<MiReserva[]> {
    const { data } = await api.get(`${BASE}/mis-reservas`);
    return data?.datos || [];
  },

  async tema(id: number): Promise<TemaPublico> {
    const { data } = await api.get(`${BASE}/temas/${id}`);
    return data?.datos;
  },

  /** Turnos desde hoy hasta la fecha de fin, en tramos de ~2 meses: si hay_mas,
   *  el siguiente tramo se pide con desde = día siguiente a `hasta`. */
  async turnos(
    id: number,
    desde?: string
  ): Promise<{ datos: Turno[]; inscripcion_abierta: boolean; hasta: string | null; hay_mas: boolean }> {
    const { data } = await api.get(`${BASE}/temas/${id}/turnos`, { params: { desde } });
    return {
      datos: data?.datos || [],
      inscripcion_abierta: data?.inscripcion_abierta !== false,
      hasta: data?.hasta || null,
      hay_mas: Boolean(data?.hay_mas),
    };
  },

  async reservar(
    id: number,
    body: { inicio: string; responsable_id: number | null; respuestas: Record<number, string>; lista_espera?: boolean }
  ): Promise<ResultadoReserva> {
    const { data } = await api.post(`${BASE}/temas/${id}/reservar`, body);
    return data;
  },

  async cancelar(reservaId: number, motivo?: string): Promise<{ info: string }> {
    const { data } = await api.post(`${BASE}/reservas/${reservaId}/cancelar`, { motivo });
    return data;
  },

  async reprogramar(
    reservaId: number,
    body: { inicio: string; responsable_id: number | null; respuestas: Record<number, string> }
  ): Promise<ResultadoReserva> {
    const { data } = await api.post(`${BASE}/reservas/${reservaId}/reprogramar`, body);
    return data;
  },

  async encuesta(reservaId: number, puntaje: number, comentario: string): Promise<{ info: string }> {
    const { data } = await api.post(`${BASE}/reservas/${reservaId}/encuesta`, { puntaje, comentario });
    return data;
  },
};

export default AgendaAPI;
